import { baseApi } from './baseApi';
import { mapModelTagToTagApi, mapTagsResponse } from '../../apiEndpoints';
import type { Tag } from '../../../types';
import type { JsonValue } from './baseQuery';

interface JsonLinks {
    next?: {
        href?: string;
    };
}

const getNextHref = (response: JsonValue | null): string | undefined => {
    if (!response || typeof response !== 'object' || Array.isArray(response) || !('_links' in response)) {
        return undefined;
    }

    const links = response._links as JsonLinks | undefined;
    return links?.next?.href;
};

export const tagsApi = baseApi.injectEndpoints({
    endpoints: build => ({
        getTags: build.query<Tag[], string>({
            async queryFn(accountId, _api, _extraOptions, baseQuery) {
                const tags: Tag[] = [];
                let nextLink: string | undefined;

                do {
                    const result = await baseQuery(
                        nextLink
                            ? {
                                  absoluteUrl: nextLink,
                              }
                            : {
                                  service: 'tags',
                                  path: '/tags',
                                  params: {
                                      account_id: accountId,
                                  },
                              }
                    );

                    if (result.error) {
                        return { error: result.error, meta: result.meta };
                    }

                    tags.push(...mapTagsResponse({ body: result.data }));
                    nextLink = getNextHref(result.data);
                } while (nextLink);

                return { data: tags };
            },
            providesTags: (result, _error, accountId) => [
                { type: 'TagList', id: accountId },
                ...(result?.map(tag => ({ type: 'Tag' as const, id: tag.id })) ?? []),
            ],
        }),
        createTag: build.mutation<Tag, { tagName: string; accountId: string }>({
            async queryFn({ tagName, accountId }, _api, _extraOptions, baseQuery) {
                const tag = mapModelTagToTagApi(tagName, accountId);
                const result = await baseQuery({
                    service: 'tags',
                    path: `/tags/${tag.id}`,
                    method: 'PUT',
                    body: tag,
                    headers: {
                        Accept: 'application/json',
                        'If-None-Match': '*',
                    },
                });

                if (result.error) {
                    return { error: result.error };
                }

                return {
                    data: {
                        id: tag.id,
                        type: tag.type,
                        name: tag.name,
                        accountId: tag.account_id,
                    },
                };
            },
            invalidatesTags: (_result, _error, args) => [{ type: 'TagList', id: args.accountId }],
        }),
    }),
});

export const { useCreateTagMutation, useGetTagsQuery } = tagsApi;
